import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { selectUser, clearUser } from '../../redux/authSlice';
import { supabase } from '../../services/supabaseClient/supabaseClient';
import wordmarkInk from '../../assets/brand/logos/wordmark-ink.svg';
import logomarkInk from '../../assets/brand/logos/logomark-ink.svg';

import './Sidebar.css';

const Sidebar = ({ open = false, onClose, onOpenTasks }) => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handleLogout = async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();

      // Clear Redux state
      dispatch(clearUser());

      // Clear all local storage
      localStorage.clear();

      // Close sidebar
      onClose();

      // Navigate to root page
      navigate('/');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Logout error:', error);
      alert('Failed to logout. Please try again.');
    }
  };

  return (
    <>
      <div
        className="sidebar-overlay"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close sidebar"
        aria-hidden={!open}
        style={{ display: open ? 'block' : 'none' }}
      />
      <aside className={`sidebar ${open ? 'open' : ''}`} aria-label="Sidebar" aria-hidden={!open}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <img src={logomarkInk} alt="" className="sidebar-brand-mark" aria-hidden="true" />
            <img src={wordmarkInk} alt="MostLearned" className="sidebar-brand-wordmark" />
          </div>
        </div>
        <div className="sidebar-content">
          {user && (
            <nav className="sidebar-nav">
              <ul>
                <li>
                  <NavLink to="/dashboard" onClick={onClose}>
                    <i className="fa-solid fa-house" aria-hidden="true" />
                    <span>Dashboard</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/analytics" onClick={onClose}>
                    <i className="fa-solid fa-chart-line" aria-hidden="true" />
                    <span>Analytics</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/classroom" onClick={onClose}>
                    <i className="fa-solid fa-robot" aria-hidden="true" />
                    <span>Classroom</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/courses" onClick={onClose}>
                    <i className="fa-solid fa-graduation-cap" aria-hidden="true" />
                    <span>Browse Courses</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/create-course" onClick={onClose}>
                    <i className="fa-solid fa-circle-plus" aria-hidden="true" />
                    <span>Create Course</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/reflection" onClick={onClose}>
                    <i className="fa-solid fa-book-open" aria-hidden="true" />
                    <span>Reflection</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/library" onClick={onClose}>
                    <i className="fa-solid fa-video" aria-hidden="true" />
                    <span>Library</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/projects" onClick={onClose}>
                    <i className="fa-solid fa-folder-open" aria-hidden="true" />
                    <span>Projects</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/prompts" onClick={onClose} className="sidebar-link--special">
                    <i className="fa-solid fa-sparkles" aria-hidden="true" />
                    <span>Prompt Studio</span>
                  </NavLink>
                </li>
                <li>
                  <a
                    href="#tasks"
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                      if (onOpenTasks) onOpenTasks();
                    }}
                  >
                    <i className="fa-solid fa-list-check" aria-hidden="true" />
                    <span>Tasks</span>
                  </a>
                </li>
                <li>
                  <NavLink to="/preferences/agent-personas" onClick={onClose}>
                    <i className="fa-solid fa-sliders" aria-hidden="true" />
                    <span>Customizations</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/profile" onClick={onClose}>
                    <i className="fa-solid fa-user-circle" aria-hidden="true" />
                    <span>Profile</span>
                  </NavLink>
                </li>
                <li>
                  <a
                    href="#logout"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLogout();
                    }}
                    className="logout-link"
                  >
                    <i className="fa-solid fa-arrow-right-from-bracket" aria-hidden="true" />
                    <span>Log Out</span>
                  </a>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
